import { useEffect, useLayoutEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { Navbar } from './Navbar';
import { OnboardingGate } from '@/components/core/guards/OnboardingGate';
import { AnalyticsIdentity } from '@/components/core/layout/AnalyticsIdentity';
import { useMediaQuery } from '@/hooks/ui/useMediaQuery';
import { PhoneChromeContext } from '@/contexts/PhoneChromeContext';

interface AppLayoutProps {
  children: React.ReactNode;
  /**
   * Below `md`, drop the app navbar and give the page the whole screen. For a
   * page that draws its own top bar with a way back out - the lesson, whose
   * reader would otherwise lose ~30% of a phone to stacked bars. The page
   * must then render `navbarAccessory` (the focus timer) itself.
   */
  hideNavbarOnPhone?: boolean;
}

/** Published on `<html>` so `ShellBanners` can sit under whatever bar is up. */
const NAVBAR_HEIGHT_VAR = '--app-navbar-height';

/**
 * sessionStorage key for the last in-app route. `/onboarding` reads it to send
 * an already-onboarded user "one step back" after a hard navigation (a typed
 * URL / refresh), where React Router's in-memory history is gone and
 * `navigate(-1)` could otherwise walk out of the app.
 */
export const LAST_APP_PATH_KEY = 'xonera:last-app-path';

export function AppLayout({ children, hideNavbarOnPhone }: AppLayoutProps) {
  const location = useLocation();
  const isPhone = useMediaQuery('(max-width: 767px)');
  // A page can also ask for it while a state lasts (`useHideNavbarOnPhone`).
  const [pageHidesNavbar, setPageHidesNavbar] = useState(false);
  const showNavbar = !((hideNavbarOnPhone || pageHidesNavbar) && isPhone);

  useLayoutEffect(() => {
    const root = document.documentElement;
    root.style.setProperty(NAVBAR_HEIGHT_VAR, showNavbar ? '4rem' : '0px');
    return () => {
      root.style.removeProperty(NAVBAR_HEIGHT_VAR);
    };
  }, [showNavbar]);

  // Remember the current app route. AppLayout wraps only app routes (never
  // /onboarding or /auth), so whatever we store is always a safe in-app
  // destination to bounce back to.
  useEffect(() => {
    sessionStorage.setItem(
      LAST_APP_PATH_KEY,
      location.pathname + location.search
    );
  }, [location]);

  // OnboardingGate fetches the user profile via React Query (which also
  // ensures the MongoDB user is created on first app load) and redirects
  // first-time users to /onboarding before any app chrome renders.
  return (
    <OnboardingGate>
      <AnalyticsIdentity />
      <PhoneChromeContext.Provider value={setPageHidesNavbar}>
        {/* `dvh`, not `h-screen`: mobile Safari's `100vh` includes the area
          behind its toolbar, which hid anything pinned to the bottom - the
          lesson chat's input and its tab bar. */}
        <div className='h-[100dvh] overflow-hidden'>
          {showNavbar && <Navbar />}

          {/* Top padding for the fixed navbar (4rem, or 0 when a page hides it
          on a phone) plus whatever status strips are up under it -
          `ShellBanners` publishes its height as `--shell-banners-height`, 0
          when none are showing. Without it a strip covered the top of the
          page instead of pushing it down. */}
          <main
            className={
              showNavbar
                ? 'h-full overflow-hidden pt-[calc(4rem+var(--shell-banners-height,0px))]'
                : 'h-full overflow-hidden pt-[var(--shell-banners-height,0px)]'
            }
          >
            {children}
          </main>
        </div>
      </PhoneChromeContext.Provider>
    </OnboardingGate>
  );
}
